import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Calendar as CalendarIcon,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Cake,
  FileEdit,
  AlertTriangle,
  Star,
  Upload,
  X,
  Clock3,
  AlarmClock,
  Palmtree,
  ChevronsLeft,
  ChevronsRight,
  Lightbulb,
  Gift,
} from "lucide-react";
import { toast } from "sonner";
import { format, subDays } from "date-fns";
import {
  createOTLog,
  getEmployeeCalendarData,
  createRegularizationRequests,
} from "@/services/attendanceService";
import {
  fetchEvents,
  getEmployees,
  fetchHolidays,
  fetchCompanyLeaveConfiguration,
} from "@/services/adminService";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { DatePickerInput } from "@/components/ui/DatePickerInput";
import { getErrorMessage } from "@/utils/errorHandler";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import TeamAvailability from "@/components/employee/TeamAvailability";

// Human-readable labels for the regularization reason codes used by the form.
const REASON_LABELS = {
  late: "Late arrival",
  missed_punch: "Missed punch",
  early_leave: "Left early",
  system_issue: "System issue",
};

// A 24-char hex string is a raw Mongo ObjectId — older records stored the
// submitter's id here instead of their name.
const isObjectId = (v) => /^[a-f0-9]{24}$/i.test(v || "");

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) =>
  i.toString().padStart(2, "0"),
);
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) =>
  i.toString().padStart(2, "0"),
);

// Simple "HH:MM" time picker (hour + minute selects) — the app has no
// dedicated time-picker component, so this mirrors the OT dialog pattern.
const TimeSelect = ({ value, onChange, disabled }) => {
  const [hh = "", mm = ""] = (value || "").split(":");
  const setPart = (h, m) => onChange(`${h || "00"}:${m || "00"}`);
  return (
    <div className="flex items-center gap-2">
      <Select value={hh} onValueChange={(h) => setPart(h, mm)} disabled={disabled}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="HH" />
        </SelectTrigger>
        <SelectContent className="max-h-56">
          {HOUR_OPTIONS.map((h) => (
            <SelectItem key={h} value={h}>
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-muted-foreground">:</span>
      <Select value={mm} onValueChange={(m) => setPart(hh, m)} disabled={disabled}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="MM" />
        </SelectTrigger>
        <SelectContent className="max-h-56">
          {MINUTE_OPTIONS.map((m) => (
            <SelectItem key={m} value={m}>
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// Human labels for the attendance issues a single day can carry. A day may have
// more than one (late in AND early out); each is regularized independently.
const ISSUE_LABELS = {
  missed_punch_in: "Missed Punch-In",
  late_arrival: "Late Arrival",
  early_punch_out: "Early Punch-Out",
};

// Default reason preselected in the form, per issue type.
const ISSUE_REASONS = {
  missed_punch_in: "missed_punch",
  late_arrival: "late",
  early_punch_out: "early_leave",
};

const EmployeeAttendance = (employeeId) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [regularizationOpen, setRegularizationOpen] = useState(false);
  // Which attendance issue the regularization dialog is currently targeting. A
  // day can carry several (late arrival + early punch-out), each regularized
  // independently, so the dialog has to know which one it is submitting for.
  const [activeIssue, setActiveIssue] = useState(null);
  const [regularizationForm, setRegularizationForm] = useState({
    date: "",
    expectedTime: "",
    outTime: "",
    reason: "",
    remarks: "",
    actualPunchIn: "",
    actualPunchOut: "",
    attachment: null,
  });
  const [documentFile, setDocumentFile] = useState(null);
  const [expectedDate, setExpectedDate] = useState([]);

  // OT logging state
  const [showOTDialog, setShowOTDialog] = useState(false);
  const [otForm, setOTForm] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    from_time: "",
    to_time: "",
    project: "",
    notes: "",
  });
  const [otLogs, setOTLogs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [weekOffs, setWeekOffs] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [regularizationLogs, setRegularizationLogs] = useState([]);
  const [employeeCalendarData, setEmployeeCalendarData] = useState({
    employeeId: 1,
    employeeName: "",
    month: "",
    attendance: [],
    summary: {
      totalDays: 0,
      present: 0,
      absent: 0,
      leave: 0,
      holidays: 0,
      lateCount: 0,
      otHours: 0,
      totalHours: 0,
    },
  });

  const [events, setEvents] = useState([]);
  const [highlights, setHighlights] = useState({});
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  // OT pagination
  const [otPage, setOtPage] = useState(1);
  const [otPageSize, setOtPageSize] = useState(5);
  const [otTimeError, setOtTimeError] = useState("");

  // ✅ Regularization pagination
  const [regPage, setRegPage] = useState(1);
  const [regPageSize, setRegPageSize] = useState(5);
  const [initialLoading, setInitialLoading] = useState(true);

  const { hasFeature } = useSubscription();

  const showRegularizationRequests = hasFeature("regularization_requests");

  const createEmptyAttendanceForDate = (date) => ({
    date: new Date(date).toISOString(),
    status: "--", // or "absent" depending on your logic
    punch_in: null,
    punch_out: null,
    early_leave_minutes: 0,
    attendance_issues: [],
    late_by_minutes: 0,
    ot_exists: false,
    ot_hours: 0,
    work_duration_minutes: 0,
    shift: null, // or default shift object if needed
  });

  const selectedDayData = useMemo(() => {
    if (!selectedDate) return null;

    const existing = employeeCalendarData?.attendance?.[selectedDate];

    if (existing) return existing;

    // 🔥 Return generated empty structure
    return createEmptyAttendanceForDate(selectedDate);
  }, [selectedDate, employeeCalendarData]);
  
  // Events/birthdays/holidays for the currently selected day, derived from the
  // CURRENT month's data. Deriving (instead of a click-set state) keeps the
  // "What's Happening" panel in sync when the month changes — events from a
  // previously viewed month can never linger.
  const dayHighlights = useMemo(() => {
    const empty = { events: [], birthdays: [], holidays: null };
    if (!selectedDate) return empty;

    const matchesSelectedDay = (rawDate) => {
      const d = new Date(rawDate);
      return (
        d.getFullYear() === selectedMonth.getFullYear() &&
        d.getMonth() === selectedMonth.getMonth() &&
        d.getDate() === selectedDate
      );
    };

    return {
      events: (events || []).filter((e) => matchesSelectedDay(e.date)),
      birthdays: (birthdays || []).filter((b) => matchesSelectedDay(b.date)),
      holidays: (holidays || []).find((h) => matchesSelectedDay(h.date)) || null,
    };
  }, [selectedDate, selectedMonth, events, birthdays, holidays]);

  const anniversariesByDay = useMemo(() => {
    const map = {};
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    (allEmployees || [])
      .filter((emp) => emp.status === "active" && emp.date_of_joining)
      .forEach((emp) => {
        const joiningDate = new Date(emp.date_of_joining);
        if (
          joiningDate.getMonth() !== month ||
          joiningDate.getFullYear() >= year
        ) {
          return;
        }
        const yearsWorked = year - joiningDate.getFullYear();
        if (yearsWorked <= 0) return;
        const day = joiningDate.getDate();
        if (!map[day]) map[day] = [];
        map[day].push({
          _id: emp._id,
          employee_id: emp.employee_id,
          employee_name: emp.name,
          yearsWorked,
          date: new Date(year, month, day),
        });
      });
    return map;
  }, [allEmployees, selectedMonth]);

  const today = new Date();

  useEffect(() => {
    fetchCalendar(selectedMonth);
  }, []);

  useEffect(() => {
    if (currentEmployee) {
      fetchUpcomingEvents();
    }
  }, [currentEmployee]);

  const fetchCalendar = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    getCalendarData(year, month);
  };

  const fetchUpcomingEvents = async () => {
    try {
      const events = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let employees = [];
      try {
        const employeesResponse = await getEmployees();
        employees = employeesResponse;
        setAllEmployees(employees);
        const activeEmployees = employees.filter(
          (emp) => emp.status === "active",
        );

        activeEmployees.forEach((emp) => {
          if (emp.date_of_birth) {
            const dob = new Date(emp.date_of_birth);
            const nextBirthday = new Date(
              today.getFullYear(),
              dob.getMonth(),
              dob.getDate(),
            );
            nextBirthday.setHours(0, 0, 0, 0);
            if (nextBirthday < today) {
              nextBirthday.setFullYear(today.getFullYear() + 1);
            }
            const daysUntilBirthday = Math.floor(
              (nextBirthday - today) / (1000 * 60 * 60 * 24),
            );
            if (daysUntilBirthday <= 30 && daysUntilBirthday >= 0) {
              events.push({
                type: "birthday",
                title: `${emp.name}'s Birthday`,
                subtitle:
                  currentEmployee && emp._id === currentEmployee._id
                    ? "(You!)"
                    : "",
                date: nextBirthday,
                daysUntil: daysUntilBirthday,
                icon: "🎂",
              });
            }
          }

          if (emp.date_of_joining) {
            const joiningDate = new Date(emp.date_of_joining);
            const yearsWorked = today.getFullYear() - joiningDate.getFullYear();
            const nextAnniversary = new Date(
              today.getFullYear(),
              joiningDate.getMonth(),
              joiningDate.getDate(),
            );
            nextAnniversary.setHours(0, 0, 0, 0);
            if (nextAnniversary < today) {
              nextAnniversary.setFullYear(today.getFullYear() + 1);
            }
            const daysUntilAnniversary = Math.floor(
              (nextAnniversary - today) / (1000 * 60 * 60 * 24),
            );
            if (
              daysUntilAnniversary <= 30 &&
              daysUntilAnniversary >= 0 &&
              yearsWorked > 0
            ) {
              events.push({
                type: "anniversary",
                title: `${emp.name}${
                  currentEmployee &&
                  emp.employee_id === currentEmployee.employee_id
                    ? " (You)"
                    : ""
                } - ${yearsWorked} Year${yearsWorked + 1 > 1 ? "s" : ""}`,
                date: nextAnniversary,
                daysUntil: daysUntilAnniversary,
                icon: "🎉",
              });
            }
          }
        });
      } catch (error) {
        console.error("Error fetching employees for events:", error);
      }

      try {
        const holidaysResponse = await fetchHolidays();
        const holidays = holidaysResponse || [];
        const upcomingHolidays = holidays
          .map((h) => ({ ...h, date: new Date(h.date) }))
          .filter((h) => {
            h.date.setHours(0, 0, 0, 0);
            const daysUntil = Math.floor(
              (h.date - today) / (1000 * 60 * 60 * 24),
            );
            return daysUntil >= 0 && daysUntil <= 30;
          })
          .sort((a, b) => a.date - b.date)
          .map((h) => ({
            type: "holiday",
            title: h.name,
            date: h.date,
            daysUntil: Math.floor((h.date - today) / (1000 * 60 * 60 * 24)),
            icon: "🎊",
          }));
        events.push(...upcomingHolidays);
      } catch (error) {
        console.error("Error fetching holidays:", error);
      }

      try {
        const adminEventsResponse = await fetchEvents();
        const adminEvents = adminEventsResponse.data.events || [];
        const processedAdminEvents = adminEvents
          .map((event) => {
            const eventDate = new Date(event.date);
            eventDate.setHours(0, 0, 0, 0);
            const daysUntil = Math.floor(
              (eventDate - today) / (1000 * 60 * 60 * 24),
            );
            const eventIcons = {
              company: "🏢",
              team: "👥",
              milestone: "🎯",
              other: "📅",
            };
            return {
              type: "event",
              title: event.title,
              subtitle: event.description || "",
              date: eventDate,
              daysUntil: daysUntil,
              icon: eventIcons[event.event_type] || "📅",
            };
          })
          .filter((event) => event.daysUntil >= 0 && event.daysUntil <= 30);
        events.push(...processedAdminEvents);
      } catch (error) {
        console.error("Error fetching admin events:", error);
      }

      try {
        const leavesResponse = await fetchCompanyLeaveConfiguration();
        const allLeaves = leavesResponse.data.leave_config || [];
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);
        const upcomingLeaves = allLeaves
          .filter((leave) => leave.status === "approved")
          .map((leave) => ({
            ...leave,
            start_date: new Date(leave.start_date),
            end_date: new Date(leave.end_date),
          }))
          .filter((leave) => {
            leave.start_date.setHours(0, 0, 0, 0);
            leave.end_date.setHours(0, 0, 0, 0);
            return leave.start_date <= nextWeek && leave.end_date >= today;
          });

        const leavesByEmployee = {};
        upcomingLeaves.forEach((leave) => {
          if (!leavesByEmployee[leave.employee_id]) {
            const employee = employees.find(
              (e) => e.employee_id === leave.employee_id,
            );
            const employeeName = employee ? employee.name : "Unknown Employee";
            leavesByEmployee[leave.employee_id] = {
              employee_id: leave.employee_id,
              employee_name: employeeName,
              leaves: [],
            };
          }
          leavesByEmployee[leave.employee_id].leaves.push(leave);
        });

        Object.values(leavesByEmployee).forEach((empLeave) => {
          const earliestLeave = empLeave.leaves.sort(
            (a, b) => a.start_date - b.start_date,
          )[0];
          const daysUntil = Math.floor(
            (earliestLeave.start_date - today) / (1000 * 60 * 60 * 24),
          );
          const isOnLeaveToday =
            earliestLeave.start_date <= today &&
            earliestLeave.end_date >= today;
          events.push({
            type: "leave",
            title: `${empLeave.employee_name}${
              currentEmployee &&
              empLeave.employee_id === currentEmployee.employee_id
                ? " (You!)"
                : ""
            }`,
            subtitle: isOnLeaveToday
              ? "On leave today"
              : `Leave starts ${format(earliestLeave.start_date, "MMM dd")}`,
            date: earliestLeave.start_date,
            daysUntil: daysUntil,
            icon: "🏖️",
          });
        });
      } catch (error) {
        console.error("Error fetching leaves:", error);
      }

      events.sort((a, b) => {
        if (a.daysUntil === b.daysUntil) {
          if (a.type === "leave" && b.type !== "leave") return -1;
          if (b.type === "leave" && a.type !== "leave") return 1;
          return 0;
        }
        return a.daysUntil - b.daysUntil;
      });

      setUpcomingEvents(events.slice(0, 6));
    } catch (error) {
      console.error("Error fetching upcoming events:", error);
    }
  };

  useEffect(() => {
    if (selectedDayData && regularizationOpen) {
      // Determine the date in "yyyy-MM-dd" format
      const formattedDate = format(selectedDayData.date, "yyyy-MM-dd");
      const shiftStart = selectedDayData.shift?.start_time || "09:00";
      const shiftEnd = selectedDayData.shift?.end_time || "17:00";

      setRegularizationForm({
        // Populate key fields from selectedDayData
        date: formattedDate,
        expectedTime: shiftStart,
        outTime: shiftEnd,
        // Default the reason to the ISSUE being regularized, not the day — a
        // day with both a late arrival and an early punch-out needs a different
        // default depending on which row was clicked.
        reason: ISSUE_REASONS[activeIssue?.event_type] || "missed_punch",
        remarks: "",
        // Prefill punch times with the shift as a sensible starting point
        // (only used for missed-punch / absent days).
        actualPunchIn: shiftStart,
        actualPunchOut: shiftEnd,
        attachment: null,
      });
      setDocumentFile(null);
    }
  }, [selectedDayData, regularizationOpen, activeIssue]);

  // Auto-select today when attendance data is loaded
  useEffect(() => {
    if (!employeeCalendarData.attendance) return;

    const todayDate = new Date().getDate();

    // If today's date exists in attendance map, select it
    if (employeeCalendarData.attendance[todayDate]) {
      setSelectedDate(todayDate);
    }
  }, [employeeCalendarData]);

  // ----------------------------
  // OT pagination derived values
  // ----------------------------
  const totalOtPages = Math.max(
    1,
    Math.ceil((otLogs?.length || 0) / otPageSize),
  );

  const paginatedOtLogs = useMemo(() => {
    const list = otLogs || [];
    const start = (otPage - 1) * otPageSize;
    return list.slice(start, start + otPageSize);
  }, [otLogs, otPage, otPageSize]);

  // --------------------------------------
  // ✅ Regularization pagination derived values
  // --------------------------------------
  const totalRegPages = Math.max(
    1,
    Math.ceil((regularizationLogs?.length || 0) / regPageSize),
  );

  useEffect(() => {
    setOtPage((p) => Math.min(p, totalOtPages));
  }, [totalOtPages]);

  useEffect(() => {
    setRegPage((p) => Math.min(p, totalRegPages));
  }, [totalRegPages]);

  function getTimeDiffHHMM(diff) {
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;

    return `${hours}h ${minutes}m`;
  }

  const isTodayDate = (day) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === selectedMonth.getMonth() &&
      today.getFullYear() === selectedMonth.getFullYear()
    );
  };

  const getCalendarData = async (year, month) => {
    try {
      const response = await getEmployeeCalendarData(year, month, employeeId?.employeeId);


      const rawDays = response.data.attendance?.days || [];

      const attendanceMap = {};

      rawDays.forEach((item) => {
        const dayNumber = new Date(item.date).getDate();
        attendanceMap[dayNumber] = item;
      });

      const totalWorkDurationMinutes = getTimeDiffHHMM(
        response.data.totalWorkDurationMinutes,
      );

      const dateObj = new Date(year, month - 1, 1);
      const formattedMonth = dateObj.toLocaleString("en-US", { month: "long" });

      setCurrentEmployee({
        _id: response.data._id,
        employee_id: response.data.employee_id,
        name: response.data.name,
      });
      setEmployeeCalendarData({
        employeeId: response.data.employee_id,
        employeeName: response.data.name,
        month: formattedMonth,
        year: year,
        attendance: attendanceMap,
        summary: {
          totalDays: response.data.totalDays,
          present: response.data.presentDays,
          absent: response.data.absentDays,
          leave: response.data.leaveDays,
          totalHours: totalWorkDurationMinutes,
          leave_balance: response.data.leave_balance,
          otHours: response.data.otHours,
        },
      });
      setHolidays(response.data.holidays);
      setLeaves(response.data.leaves);
      setWeekOffs(response.data.weekOffs);
      setBirthdays(response.data.birthdays);
      setEvents(response.data.companyEvents);
      setOTLogs(response.data.monthlyOTLogs);
      setRegularizationLogs(response.data.monthlyRegularizationLog);

      // ✅ reset pagination when new month data arrives
      setOtPage(1);
      setRegPage(1);
    } catch (err) {
      console.error("Calendar fetch error:", err);
    } finally {
      setInitialLoading(false);
    }
  };

  const PaginationBar = ({
    page,
    setPage,
    pageSize,
    setPageSize,
    totalItems,
    totalPages,
    label = "items",
  }) => {
    const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, totalItems);

    const goToPage = (p) => {
      const next = Math.min(Math.max(1, p), totalPages);
      setPage(next);
    };

    return (
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 py-3 mt-2 border-t px-2">
        {/* Left: Stats */}
        <div className="text-[13px] text-muted-foreground order-2 md:order-1">
          Showing{" "}
          <span className="font-medium text-foreground">
            {start}-{end}
          </span>{" "}
          of <span className="font-medium text-foreground">{totalItems}</span>{" "}
          {label}
        </div>

        {/* Right: Controls Group */}
        <div className="flex items-center gap-6 order-1 md:order-2">
          {/* Rows per page */}
          <div className="flex items-center gap-2">
            <p className="hidden sm:block text-[13px] font-medium text-muted-foreground">
              Rows per page
            </p>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[70px] text-xs">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {[5, 10, 20, 50].map((n) => (
                  <SelectItem key={n} value={String(n)} className="text-xs">
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Page counter */}
          <div className="flex items-center justify-center text-[13px] font-medium min-w-[80px]">
            Page {page} of {totalPages}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => goToPage(1)}
              disabled={page <= 1}
            >
              <span className="sr-only">Go to first page</span>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => goToPage(totalPages)}
              disabled={page >= totalPages}
            >
              <span className="sr-only">Go to last page</span>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const getToDaysAttendance = () => {
    const attendanceArr = Array.isArray(employeeCalendarData.attendance)
      ? employeeCalendarData.attendance
      : Object.values(employeeCalendarData.attendance || {});

    const attendanceDay = attendanceArr.find(
      (day) =>
        new Date(day.date).toISOString().split("T")[0] ===
        format(today, "yyyy-MM-dd"),
    );
    setExpectedDate(attendanceDay);
  };

  const parseTimeToMinutes = (time) => {
    if (!time || !time.includes(":")) return null;

    const [hours, minutes] = time.split(":").map(Number);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

    return hours * 60 + minutes;
  };

  const fromHour = otForm.from_time.split(":")[0] || "";
  const fromMinute = otForm.from_time.split(":")[1] || "";
  const toHour = otForm.to_time.split(":")[0] || "";
  const toMinute = otForm.to_time.split(":")[1] || "";
  const selectedFromTimeMinutes = parseTimeToMinutes(otForm.from_time);
  const selectedToTimeMinutes = parseTimeToMinutes(otForm.to_time);

  const isToHourDisabled = (hourValue) => {
    if (!fromHour) return false;
    return Number(hourValue) < Number(fromHour);
  };

  const isToMinuteDisabled = (minuteValue) => {
    if (!fromHour || !toHour) return false;

    const currentToMinutes = Number(toHour) * 60 + Number(minuteValue);
    return currentToMinutes <= selectedFromTimeMinutes;
  };

  useEffect(() => {
    if (
      selectedFromTimeMinutes !== null &&
      selectedToTimeMinutes !== null &&
      selectedToTimeMinutes < selectedFromTimeMinutes
    ) {
      setOTForm((prev) => ({
        ...prev,
        to_time: "",
      }));
    }
  }, [selectedFromTimeMinutes, selectedToTimeMinutes]);

  const handleOTSubmit = async (e) => {
    e.preventDefault();

    const selectedDate = otForm.date; // expected "yyyy-MM-dd"

    // -----------------------------
    // Validate time format (minutes must be 00 or 30)
    // -----------------------------
    const fromMinutesPart = otForm.from_time.split(":")[1];
    const toMinutesPart = otForm.to_time.split(":")[1];

    const parseTimeToMinutes = (t) => {
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    };

    // -----------------------------
    // Validate TO time is after FROM time
    // -----------------------------
    const otFromMinutes = parseTimeToMinutes(otForm.from_time);
    const otToMinutes = parseTimeToMinutes(otForm.to_time);

    if (otToMinutes <= otFromMinutes) {
      toast.error("To time must be after From time");
      return;
    }

    // ======================================================
    // ⭐ SHIFT OVERLAP VALIDATION (only for PRESENT / LATE)
    // OT must be fully BEFORE shift start OR fully AFTER shift end
    // ======================================================
    const attendanceArr = Array.isArray(employeeCalendarData.attendance)
      ? employeeCalendarData.attendance
      : Object.values(employeeCalendarData.attendance || {});

    const attendanceDay = attendanceArr.find(
      (day) => new Date(day.date).toISOString().split("T")[0] === selectedDate,
    );

    if (attendanceDay?.shift) {
      const rawStatus = (attendanceDay.status ?? "")
        .toString()
        .trim()
        .toUpperCase();
      const shouldValidateAgainstShift =
        rawStatus === "PRESENT" || rawStatus === "LATE";

      if (shouldValidateAgainstShift) {
        const shiftStart = attendanceDay.shift.start_time; // e.g. "09:00"
        const shiftEnd = attendanceDay.shift.end_time; // e.g. "18:30"

        // If both times exist, enforce NO OVERLAP
        if (shiftStart && shiftEnd) {
          const shiftStartMinutes = parseTimeToMinutes(shiftStart);
          const shiftEndMinutes = parseTimeToMinutes(shiftEnd);

          // overlap if OT starts before shift ends AND OT ends after shift starts
          const overlapsShift =
            otFromMinutes < shiftEndMinutes && otToMinutes > shiftStartMinutes;

          if (overlapsShift) {
            toast.error(
              `OT must not overlap shift time (${shiftStart} - ${shiftEnd}). ` +
                `Choose OT fully before shift start OR fully after shift end.`,
            );
            return;
          }
        } else if (shiftEnd) {
          // Fallback: if only shift end exists, enforce OT starts after shift end
          const shiftEndMinutes = parseTimeToMinutes(shiftEnd);

          if (otFromMinutes <= shiftEndMinutes) {
            toast.error(
              `OT Start Time (${otForm.from_time}) must be AFTER shift end time (${shiftEnd}) for the selected date.`,
            );
            return;
          }
        }
        // If shiftStart/shiftEnd missing entirely: skip validation (can't reliably validate)
      }
      // else: status not PRESENT/LATE -> allow OT any time (no shift overlap validation)
    }

    try {
      setLoading(true);
      await createOTLog(otForm);
      toast.success("OT hours logged successfully!");
      setShowOTDialog(false);
      setOTForm({
        date: format(new Date(), "yyyy-MM-dd"),
        from_time: "",
        to_time: "",
        project: "",
        notes: "",
      });
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      getCalendarData(currentYear, currentMonth);
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to log OT hours");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      // ✅ PRESENT / GOOD
      present:
        "bg-emerald-100/70 dark:bg-emerald-900/25 text-emerald-800 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-500/30",

      // ⏰ LATE (warning, not error)
      late: "bg-amber-100/70 dark:bg-amber-900/25 text-amber-800 dark:text-amber-300 border border-amber-200/60 dark:border-amber-500/30",

      // ❌ ABSENT / LEAVE (problem)
      absent:
        "bg-rose-100/70 dark:bg-rose-900/25 text-rose-800 dark:text-rose-300 border border-rose-200/60 dark:border-rose-500/30",
      leave:
        "bg-rose-100/70 dark:bg-rose-900/25 text-rose-800 dark:text-rose-300 border border-rose-200/60 dark:border-rose-500/30",
      "on-leave":
        "bg-rose-100/70 dark:bg-rose-900/25 text-rose-800 dark:text-rose-300 border border-rose-200/60 dark:border-rose-500/30",

      // 🌓 HALF DAY
      "half-day":
        "bg-yellow-100/70 dark:bg-yellow-900/25 text-yellow-800 dark:text-yellow-300 border border-yellow-200/60 dark:border-yellow-500/30",

      // 🎉 HOLIDAY / OFF
      holiday:
        "bg-sky-100/70 dark:bg-sky-900/25 text-sky-800 dark:text-sky-300 border border-sky-200/60 dark:border-sky-500/30",
      "week-off":
        "bg-slate-100/70 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300 border border-slate-200/60 dark:border-slate-600/40",
      weekend:
        "bg-slate-100/70 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300 border border-slate-200/60 dark:border-slate-600/40",

      // ⏱️ OVERTIME
      overtime:
        "bg-indigo-100/70 dark:bg-indigo-900/25 text-indigo-800 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-500/30",
    };

    return (
      colors[status] ||
      "bg-gray-100 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700"
    );
  };

  const getStatusIcon = (status, durationText, dayDate) => {
    // Styles for the combined icon/text span
    const combinedClasses = "text-xs mt-1 inline-flex items-center gap-1";
    if (status === "holiday") {
      console.log("Debug: Day date is Christmas 2025");
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const cellDate = new Date(dayDate);
    cellDate.setHours(0, 0, 0, 0);

    const isFuture = cellDate > today;

    switch (status) {
      case "present":
        return (
          // Green Check + Duration Text
          <span className={combinedClasses}>
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            {durationText}
          </span>
        );
      case "late":
        return (
          // Green Check + Duration Text
          <span className={combinedClasses}>
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            {durationText}
          </span>
        );
      case "half-day":
        return (
          <span className={combinedClasses}>
            {!isFuture && (
              <Clock3 className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
            )}
            {!isFuture && durationText}
          </span>
        );

      case "absent":
        // Red Alert for Absent (distinction from scheduled leave)
        return (
          <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
        );

      case "leave":
      case "on-leave":
        // Red X Circle for Scheduled Leave
        return <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />;

      case "week-off":
        // Gray Calendar Icon for Scheduled Non-Working Day
        return (
          <CalendarIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        );

      case "holiday":
        // Orange Star for Holiday
        return <Star className="h-4 w-4 text-red-600 dark:text-red-400" />;

      default:
        // Gray Clock for Default/Pending Status
        return <Clock className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
    }
  };

  const formatMinutesToHoursMinutes = (totalMinutes) => {
    const minutes = Math.floor(totalMinutes) || 0;
    if (minutes <= 0) return null;

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    // Function to pad a number with a leading zero if it's less than 10
    const pad = (num) => String(num).padStart(2, "0");

    // Format hours and minutes using padding
    const paddedHours = pad(hours);
    const paddedMinutes = pad(remainingMinutes);

    return `${paddedHours}h${paddedMinutes}m`;
  };

  // Helper function to format raw hours (potentially decimal) into "XXhYYm" string
  const formatHoursToHoursMinutes = (totalHours) => {
    const hours = totalHours || 0;
    if (hours <= 0) return null;

    const totalMinutes = Math.round(hours * 60);
    return formatMinutesToHoursMinutes(totalMinutes);
  };

  const getAttendanceDisplay = (dayData) => {
    const { status, work_duration_minutes } = dayData;
    const minutes = work_duration_minutes || 0;

    // --- 1. Calculate main duration text (using the helper) ---
    const durationText = formatMinutesToHoursMinutes(minutes) || "";

    // --- 2. Format OT and Late values ---
    const otDisplay = formatHoursToHoursMinutes(dayData?.ot_hours);
    const lateDisplay = formatMinutesToHoursMinutes(dayData?.late_by_minutes);

    // --- 3. Return the complete, absolutely positioned display block ---
    return (
      // This container holds the positioning, vertical stack, and gap
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 translate-y-2 flex flex-col items-center leading-none text-xs font-medium gap-1">
        {/* 1. Main Status Icon/Time */}
        {getStatusIcon(status, durationText, dayData.date)}

        {/* 2. Overtime (OT) Display */}
        {otDisplay && (
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3 text-blue-600 dark:text-blue-400" />
            {otDisplay}
          </span>
        )}

        {/* 3. Late Arrival Display */}
        {lateDisplay && (
          <span className="inline-flex items-center gap-1">
            <AlertCircle className="h-3 w-3 text-orange-600 dark:text-orange-400" />
            {lateDisplay}
          </span>
        )}
      </div>
    );
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowed = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowed.includes(file.type))
      return toast.error("Only PDF, JPG, PNG allowed");

    if (file.size > 5 * 1024 * 1024)
      return toast.error("File too large (max 5MB)");

    setDocumentFile(file);
  };

  const handleFormChange = (field, value) => {
    setRegularizationForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const submitRegularization = async () => {
    const {
      date,
      expectedTime,
      outTime,
      reason,
      remarks,
      actualPunchIn,
      actualPunchOut,
    } = regularizationForm;

    if (!date || !expectedTime || !outTime || !reason) {
      toast.error("Please fill the required fields");
      return;
    }

    if (!remarks || !remarks.trim()) {
      toast.error("Please add remarks explaining the request");
      return;
    }

    // Missed-punch days must supply the times actually worked. Keyed off the
    // ISSUE being regularized, so a late-arrival row on an absent day does not
    // demand punch times it does not need.
    const isMissedPunch = activeIssue
      ? activeIssue.event_type === "missed_punch_in"
      : selectedDayData?.status === "absent" ||
        (selectedDayData?.status === "on-leave" &&
          selectedDayData?.is_unpaid_absence);

    if (isMissedPunch && (!actualPunchIn || !actualPunchOut)) {
      toast.error("Please enter the actual punch-in and punch-out times");
      return;
    }

    const formData = new FormData();
    formData.append("date", date);
    formData.append("expectedTime", expectedTime);
    formData.append("outTime", outTime);
    formData.append("reason", reason);
    formData.append("remarks", remarks);
    formData.append("actualPunchIn", actualPunchIn || "");
    formData.append("actualPunchOut", actualPunchOut || "");
    // Target the exact issue being regularized. A day can carry both a
    // late-arrival and an early-punch-out record, each regularized separately —
    // the id pins the record, and event_type lets the backend resolve it when
    // no record exists yet (a missed punch-in creates one on submit).
    if (activeIssue?._id) {
      formData.append("latearrivalId", activeIssue._id);
    }
    if (activeIssue?.event_type) {
      formData.append("event_type", activeIssue.event_type);
    }
    // Attachment is held in `documentFile` (the file input never wrote it
    // back to the form object).
    if (documentFile) formData.append("attachment", documentFile);

    try {
      await createRegularizationRequests(formData);

      toast.success("Regularization submitted successfully!");

      // Reset form
      setRegularizationForm({
        date: "",
        expectedTime: "",
        outTime: "",
        reason: "",
        remarks: "",
        actualPunchIn: "",
        actualPunchOut: "",
        attachment: null,
      });
      setDocumentFile(null);
      setRegularizationOpen(false);
      setActiveIssue(null);
      // Refresh the calendar so the day now reflects the pending request.
      fetchCalendar(selectedMonth);
    } catch (error) {
      console.error(error);
      toast.error(getErrorMessage(error, "Failed to submit regularization"));
    }
  };

  const handleDateClick = (date) => {
    console.log("handleDateClick", date);
    setSelectedDate(date);
  };

  // Selecting a day + scrolling the "Day Details" card into view. Used by the
  // "Regularize" badge so tapping it jumps straight to the details/apply card.
  const dayDetailsRef = useRef(null);
  const focusDayDetails = (date) => {
    handleDateClick(date);
    // Wait for the details card to (re)render for the newly selected day.
    setTimeout(() => {
      dayDetailsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 60);
  };

  const LegendItem = ({ icon, label }) => (
    <div className="flex items-center gap-2 text-muted-foreground">
      {icon}
      <span>{label}</span>
    </div>
  );

  const getCalendarCellClass = (status, dayDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isPastOrToday = dayDate <= today;

    switch (status) {
      case "half-day":
        return isPastOrToday
          ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-300 border-emerald-300"
          : "bg-muted/40 text-muted-foreground border-border";

      case "late":
        return isPastOrToday
          ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-300 border-emerald-300"
          : "bg-muted/40 text-muted-foreground border-border";

      case "present":
        return "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-300 border-emerald-300";

      case "absent":
      case "leave":
      case "on-leave":
        return "bg-rose-200 dark:bg-rose-900/50 text-rose-900 dark:text-rose-300 border-rose-400";

      case "holiday":
      case "week-off":
      case "weekend":
        return "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800 border-l-4";

      default:
        return "bg-muted/40 text-muted-foreground border-border";
    }
  };

  // A day is "regularizable" when the employee can still raise a regularization
  // request for it: a late arrival, an absent day, or an auto unpaid-leave day
  // (an unpaid absence is surfaced as "on-leave" + is_unpaid_absence). We only
  // nudge on the cell when the feature is enabled, the day is today/in the past,
  // and no request has been raised (and it isn't already regularized) yet.
  const isRegularizableDay = (dayData, cellDate) => {
    if (!showRegularizationRequests || !dayData || !cellDate) return false;

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const day = new Date(cellDate);
    day.setHours(0, 0, 0, 0);
    if (day > startOfToday) return false; // future days can't be regularized

    const issues = Array.isArray(dayData.attendance_issues)
      ? dayData.attendance_issues
      : [];

    // Same rule as the day detail: a delay inside the grace period is not a
    // late arrival, so it must not flag the calendar either.
    const graceMins = Number(dayData.shift?.grace_time) || 0;
    const isLate = Number(dayData.late_by_minutes || 0) > graceMins;
    const isMissedPunch =
      dayData.status === "absent" ||
      (dayData.status === "on-leave" && dayData.is_unpaid_absence);
    if (!isLate && !isMissedPunch && issues.length === 0) return false;

    if (dayData.is_regularized) return false;

    // Nudge while ANY issue on the day is still unregularized — a day whose
    // late arrival is approved but whose early punch-out is untouched still
    // needs the employee's attention.
    const hasUnhandledIssue = issues.some(
      (issue) =>
        !["pending", "approved"].includes(issue?.regularization?.status)
    );

    if (issues.length > 0) return hasUnhandledIssue;

    // No record yet (absent day) → nudge unless the day already carries one.
    return !dayData.regularization?.status;
  };

  const renderCalendar = () => {
    const weeks = [];
    let days = [];

    const daysInMonth = new Date(
      selectedMonth.getFullYear(),
      selectedMonth.getMonth() + 1,
      0,
    ).getDate();

    const firstDayOfWeek = (() => {
      const d = new Date(
        selectedMonth.getFullYear(),
        selectedMonth.getMonth(),
        1,
      ).getDay();

      // Convert Sunday-0 → 6, Monday-1 → 0, etc.
      return (d + 6) % 7;
    })();

    // Empty cells before month start
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="aspect-square" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dayData = employeeCalendarData.attendance
        ? employeeCalendarData.attendance[day]
        : null;

      const cellDate = new Date(
        selectedMonth.getFullYear(),
        selectedMonth.getMonth(),
        day,
      );
      cellDate.setHours(0, 0, 0, 0);

      const isFuture = cellDate > today;

      const holidayForDay = holidays.find((h) => {
        const holidayDate = new Date(h.date);
        return (
          holidayDate.getFullYear() === selectedMonth.getFullYear() &&
          holidayDate.getMonth() === selectedMonth.getMonth() &&
          holidayDate.getDate() === day
        );
      });

      const leaveForDay = leaves.find((l) => {
        const leaveDate = new Date(l.date);
        return (
          leaveDate.getFullYear() === selectedMonth.getFullYear() &&
          leaveDate.getMonth() === selectedMonth.getMonth() &&
          leaveDate.getDate() === day
        );
      });

      const weekOffForDay = weekOffs.find((w) => {
        const weekOffDate = new Date(w.date);
        return (
          weekOffDate.getFullYear() === selectedMonth.getFullYear() &&
          weekOffDate.getMonth() === selectedMonth.getMonth() &&
          weekOffDate.getDate() === day
        );
      });

      const birthdaysForDay = (birthdays || []).filter((b) => {
        const bDate = new Date(b.date);
        return (
          bDate.getFullYear() === selectedMonth.getFullYear() &&
          bDate.getMonth() === selectedMonth.getMonth() &&
          bDate.getDate() === day
        );
      });

      const eventsForDay = (events || []).filter((b) => {
        const bDate = new Date(b.date);
        return (
          bDate.getFullYear() === selectedMonth.getFullYear() &&
          bDate.getMonth() === selectedMonth.getMonth() &&
          bDate.getDate() === day
        );
      });

      const anniversariesForDay = anniversariesByDay[day] || [];
      // Priority: dayData > holiday > leave > week-off
      // Create merged data for display
      const displayData =
        dayData ||
        (leaveForDay
          ? {
              status: leaveForDay.type === "half-day" ? "half-day" : "on-leave",
              shift: null,
            }
          : weekOffForDay
            ? {
                status: "week-off",
                shift: null,
              }
            : null);

      const isVisibleFutureStatus =
        displayData &&
        [
          "leave",
          "on-leave",
          "half-day",
          "absent",
          "week-off",
          "holiday",
        ].includes(displayData.status);

      const shouldShowStatus =
        displayData && (!isFuture || isVisibleFutureStatus);

      // Determine status class with priority
      const statusClass = holidayForDay
        ? getCalendarCellClass("holiday", cellDate)
        : leaveForDay && !dayData
          ? getCalendarCellClass(
              leaveForDay.type === "half-day" ? "half-day" : "on-leave",
              cellDate,
            )
          : weekOffForDay && !dayData && !leaveForDay
            ? getCalendarCellClass("week-off", cellDate)
            : shouldShowStatus
              ? getCalendarCellClass(displayData.status, cellDate)
              : "bg-muted/30 text-muted-foreground border-border";

      days.push(
        <button
          key={day}
          onClick={() => {
            handleDateClick(day);
            setHighlights({
              events: eventsForDay,
              birthdays: birthdaysForDay,
              holidays: holidayForDay,
              anniversaries: anniversariesForDay,
            });
          }}
          className={`relative aspect-square rounded-xl border-2 p-2 transition-all hover:scale-105
      ${statusClass}
      ${
        selectedDate === day
          ? "ring-2 ring-primary ring-offset-2 ring-offset-background"
          : ""
      }
      ${
        isTodayDate(day) && selectedDate !== day
          ? "outline outline-2 outline-blue-400 dark:outline-blue-500"
          : ""
      }
    `}
        >
          {/* BADGE STACK — top-left */}
          <div className="absolute top-1 left-1 flex flex-col gap-1 max-w-[85%]">
            {/* HOLIDAY */}
            {holidayForDay && (
              <span className="truncate rounded-md bg-amber-500/15 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-[10px] font-semibold">
                {holidayForDay.name}
              </span>
            )}

            {/* SHIFT — only if NOT holiday/weekoff/leave */}
            {displayData?.shift?.name &&
              !holidayForDay &&
              !leaveForDay &&
              !weekOffForDay &&
              ["present", "late", "half-day"].includes(displayData.status) && (
                <span className="truncate rounded-md bg-muted/40 px-1 py-0.5 text-[10px] font-semibold text-muted-foreground">
                  {displayData.shift?.name}
                </span>
              )}

            {/* BIRTHDAYS */}
            {birthdaysForDay.length > 0 && (
              <div
                className="flex items-center gap-1 rounded-md bg-pink-100/80 dark:bg-pink-900/40 px-1.5 py-0.5"
                title={birthdaysForDay.map((b) => b.employee_name).join(", ")}
              >
                <Cake className="h-3.5 w-3.5 text-pink-600 dark:text-pink-400 shrink-0" />

                {birthdaysForDay.length === 1 && (
                  <span className="truncate text-[10px] font-semibold text-pink-700 dark:text-pink-300">
                    {birthdaysForDay[0].employee_name}
                  </span>
                )}

                {birthdaysForDay.length > 1 && (
                  <span className="text-[10px] font-bold text-pink-700 dark:text-pink-300">
                    +{birthdaysForDay.length}
                  </span>
                )}
              </div>
            )}

            {eventsForDay.length > 0 && (
              <div
                className="flex items-center gap-1 rounded-md bg-pink-100/80 dark:bg-pink-900/40 px-1.5 py-0.5"
                title={eventsForDay.map((b) => b.title).join(", ")}
              >
                <AlarmClock className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400 shrink-0" />

                {eventsForDay.length === 1 && (
                  <span className="truncate text-[10px] font-semibold text-purple-700 dark:text-purple-300">
                    {eventsForDay[0].title}
                  </span>
                )}

                {eventsForDay.length > 1 && (
                  <span className="text-[10px] font-bold text-purple-700 dark:text-purple-300">
                    +{eventsForDay.length}
                  </span>
                )}
              </div>
            )}

            {anniversariesForDay.length > 0 && (
              <div
                className="flex items-center gap-1 rounded-md bg-emerald-100/80 dark:bg-emerald-900/40 px-1.5 py-0.5"
                title={anniversariesForDay
                  .map((a) => `${a.employee_name} (${a.yearsWorked}y)`)
                  .join(", ")}
              >
                <Gift className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />

                {anniversariesForDay.length === 1 && (
                  <span className="truncate text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                    {anniversariesForDay[0].employee_name}
                  </span>
                )}

                {anniversariesForDay.length > 1 && (
                  <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300">
                    +{anniversariesForDay.length}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* REGULARIZATION NUDGE — day is still eligible to be regularized */}
          {isRegularizableDay(dayData, cellDate) && (
            <span
              role="button"
              title="Apply for regularization — click to open this day's details"
              onClick={(e) => {
                e.stopPropagation();
                focusDayDetails(day);
              }}
              className="absolute top-1 right-1 z-10 flex cursor-pointer items-center gap-0.5 rounded-full bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold text-white shadow-sm hover:bg-amber-600"
            >
              <AlertCircle className="h-3 w-3 shrink-0 animate-pulse" />
              <span className="hidden lg:inline">Regularize</span>
            </span>
          )}

          {/* DAY NUMBER */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-bold">{day}</span>
          </div>

          {/* STATUS / TIME DISPLAY */}
          {shouldShowStatus && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 translate-y-2 flex flex-col items-center gap-1 text-xs font-medium">
              {getAttendanceDisplay(displayData)}
            </div>
          )}
        </button>,
      );

      if (days.length === 7) {
        weeks.push(
          <div key={`week-${weeks.length}`} className="grid grid-cols-7 gap-2">
            {days}
          </div>,
        );
        days = [];
      }
    }

    // Fill remaining cells
    if (days.length > 0) {
      while (days.length < 7) {
        days.push(
          <div key={`empty-end-${days.length}`} className="aspect-square" />,
        );
      }
      weeks.push(
        <div key={`week-${weeks.length}`} className="grid grid-cols-7 gap-2">
          {days}
        </div>,
      );
    }

    return weeks;
  };

  const calendarLegendMap = [
    {
      label: "Present / Half-Day / Late",
      className: "bg-emerald-100 dark:bg-emerald-900/30 border-emerald-300",
    },
    {
      label: "Absent / Leave",
      className:
        "bg-slate-500/10 dark:bg-slate-400/10 border-slate-400/30 dark:border-slate-600/40",
    },
    {
      label: "Holiday",
      className: "bg-rose-200 dark:bg-rose-900/50 border-rose-400",
    },
    {
      label: "Week Off",
      className:
        "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800",
    },
    {
      label: "Birthday",
      className:
        "bg-pink-50 dark:bg-pink-950/30 border-pink-200 dark:border-pink-800",
    },
    {
      label: "Event",
      className:
        "bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800",
    },
    {
      label: "Work Anniversary",
      className:
        "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800",
    },
    ...(showRegularizationRequests
      ? [
          {
            label: "Regularize",
            // Mini replica of the on-cell badge (amber pill + AlertCircle) so
            // the legend matches what's actually shown on the day cells.
            icon: (
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white">
              </span>
            ),
          },
        ]
      : []),
  ];

  const MobileCalendarLegend = () => {
    return (
      <div className="mt-3">
        <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1">
          {calendarLegendMap.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 min-w-max">
              {item.icon || (
                <div className={`w-4 h-4 rounded-sm border ${item.className}`} />
              )}
              <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMobileCalendar = () => {
    const weeks = [];
    let days = [];
    const getCalendarCellClass = (status, dayDate) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const isPastOrToday = dayDate <= today;

      switch (status) {
        case "half-day":
          return isPastOrToday
            ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-300 border-emerald-300"
            : "bg-muted/40 text-muted-foreground border-border";

        case "late":
          return isPastOrToday
            ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-300 border-emerald-300"
            : "bg-muted/40 text-muted-foreground border-border";

        case "present":
          return "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-300 border-emerald-300";

        case "absent":
        case "leave":
        case "on-leave":
          return `
      bg-slate-500/10 dark:bg-slate-400/10
      text-slate-700 dark:text-slate-300
      border-slate-400/30 dark:border-slate-600/40
    `;
        case "holiday":
          return "bg-rose-200 dark:bg-rose-900/50 text-rose-900 dark:text-rose-300 border-rose-400";
        case "week-off":
        case "weekend":
          return "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800";

        case "birthday":
          return "bg-pink-50 dark:bg-pink-950/30 text-pink-700 dark:text-pink-400 border-pink-200 dark:border-pink-800";

        case "event":
          return "bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800";

        default:
          return "bg-muted/40 text-muted-foreground border-border";
      }
    };
    const daysInMonth = new Date(
      selectedMonth.getFullYear(),
      selectedMonth.getMonth() + 1,
      0,
    ).getDate();

    const firstDayOfWeek = (() => {
      const d = new Date(
        selectedMonth.getFullYear(),
        selectedMonth.getMonth(),
        1,
      ).getDay();

      // Convert Sunday-0 → 6, Monday-1 → 0, etc.
      return (d + 6) % 7;
    })();

    // Empty cells before month start
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="aspect-square" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dayData = employeeCalendarData.attendance
        ? employeeCalendarData.attendance[day]
        : null;

      const cellDate = new Date(
        selectedMonth.getFullYear(),
        selectedMonth.getMonth(),
        day,
      );
      cellDate.setHours(0, 0, 0, 0);

      const isFuture = cellDate > today;

      const holidayForDay = holidays.find((h) => {
        const holidayDate = new Date(h.date);
        return (
          holidayDate.getFullYear() === selectedMonth.getFullYear() &&
          holidayDate.getMonth() === selectedMonth.getMonth() &&
          holidayDate.getDate() === day
        );
      });
      const leaveForDay = leaves.find((l) => {
        const leaveDate = new Date(l.date);
        return (
          leaveDate.getFullYear() === selectedMonth.getFullYear() &&
          leaveDate.getMonth() === selectedMonth.getMonth() &&
          leaveDate.getDate() === day
        );
      });

      const weekOffForDay = weekOffs.find((w) => {
        const weekOffDate = new Date(w.date);
        return (
          weekOffDate.getFullYear() === selectedMonth.getFullYear() &&
          weekOffDate.getMonth() === selectedMonth.getMonth() &&
          weekOffDate.getDate() === day
        );
      });

      const birthdaysForDay = (birthdays || []).filter((b) => {
        const bDate = new Date(b.date);
        return (
          bDate.getFullYear() === selectedMonth.getFullYear() &&
          bDate.getMonth() === selectedMonth.getMonth() &&
          bDate.getDate() === day
        );
      });
      const eventsForDay = (events || []).filter((b) => {
        const bDate = new Date(b.date);
        return (
          bDate.getFullYear() === selectedMonth.getFullYear() &&
          bDate.getMonth() === selectedMonth.getMonth() &&
          bDate.getDate() === day
        );
      });
      const anniversariesForDay = anniversariesByDay[day] || [];
      const isVisibleFutureStatus =
        dayData &&
        [
          "leave",
          "on-leave",
          "half-day",
          "absent",
          "week-off",
          "holiday",
        ].includes(dayData.status);

      const shouldShowStatus = dayData && (!isFuture || isVisibleFutureStatus);

      let statusClass;

      if (holidayForDay) {
        statusClass = getCalendarCellClass("holiday", cellDate);
      } else if (leaveForDay && !dayData) {
        statusClass = getCalendarCellClass(
          leaveForDay.type === "half-day" ? "half-day" : "on-leave",
          cellDate,
        );
      } else if (weekOffForDay && !dayData && !leaveForDay) {
        statusClass = getCalendarCellClass("week-off", cellDate);
      } else if (shouldShowStatus && dayData) {
        statusClass = getCalendarCellClass(dayData.status, cellDate);
      } else if (birthdaysForDay && birthdaysForDay.length > 0) {
        statusClass = getCalendarCellClass("birthday", cellDate);
      } else if (eventsForDay && eventsForDay.length > 0) {
        statusClass = getCalendarCellClass("event", cellDate);
      } else if (anniversariesForDay && anniversariesForDay.length > 0) {
        statusClass = getCalendarCellClass("event", cellDate);
      } else {
        statusClass = "bg-muted/30 text-muted-foreground border-border";
      }

      days.push(
        <button
          key={day}
          onClick={() => {
            handleDateClick(day);
            setHighlights({
              events: eventsForDay,
              birthdays: birthdaysForDay,
              holidays: holidayForDay,
              anniversaries: anniversariesForDay,
            });
          }}
          className={`relative aspect-square border-1
          ${statusClass}
          ${
            selectedDate === day
              ? "border-2s border-offset-2 border shadow-md"
              : ""
          }
          ${
            isTodayDate(day) && selectedDate !== day
              ? "border border-1 border-primary"
              : ""
          }
        `}
        >
          {dayData?.status === "half-day" && (
            <span className="absolute bottom-1 right-1  bg-amber-500 text-white text-[9px] font-black px-1 py-0.5 leading-none">
              ½
            </span>
          )}

          {/* REGULARIZATION NUDGE — day is still eligible to be regularized */}
          {isRegularizableDay(dayData, cellDate) && (
            <span
              role="button"
              title="Apply for regularization — tap to open this day's details"
              onClick={(e) => {
                e.stopPropagation();
                focusDayDetails(day);
              }}
              className="absolute top-0.5 right-0.5 z-10 h-2 w-2 cursor-pointer rounded-full bg-amber-500"
            />
          )}

          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span
              className={`text-base font-bold ${
                selectedDate === day ? "text-gray" : ""
              }`}
            >
              {day}
            </span>
          </div>
        </button>,
      );

      if (days.length === 7) {
        weeks.push(
          <div
            key={`week-${weeks.length}`}
            className="grid grid-cols-7 gap-x-1"
          >
            {days}
          </div>,
        );
        days = [];
      }
    }

    // Fill remaining cells
    if (days.length > 0) {
      while (days.length < 7) {
        days.push(
          <div key={`empty-end-${days.length}`} className="aspect-square" />,
        );
      }
      weeks.push(
        <div key={`week-${weeks.length}`} className="grid grid-cols-7 gap-x-1">
          {days}
        </div>,
      );
    }

    return weeks;
  };

  const DetailColumn = ({ label, value, valueClass = "text-foreground" }) => (
    <div className="flex flex-col items-center flex-1 min-w-0">
      <span className="text-xs text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
        {label}
      </span>
      <span className={`text-sm font-medium whitespace-nowrap ${valueClass}`}>
        {value}
      </span>
    </div>
  );

  const inTime = selectedDayData?.punch_in
    ? selectedDayData.punch_in
    : selectedDayData?.shift?.start_time || "--";

  const outTime = selectedDayData?.punch_out
    ? selectedDayData.punch_out
    : selectedDayData?.shift?.end_time || "--";

  const calculateArrivedAtTime = (shiftStart, lateMinutes) => {
    if (!shiftStart || lateMinutes <= 0) return "--";

    const [hh, mm] = shiftStart.split(":").map(Number);
    const date = new Date();
    date.setHours(hh, mm + lateMinutes, 0, 0);

    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // A late day already has a LateArrival; a "forgot to punch in" day is
  // absent (or shown as unpaid on-leave); an early punch-out day carries an
  // early_punch_out record and is docked to a half-day. All can be regularized.
  // A delay only counts as a LATE ARRIVAL once it exceeds the shift's grace
  // period. `late_by_minutes` records the real delay for ANY arrival, including
  // one inside grace — an employee who is 10 minutes late on a 15-minute grace
  // was on time by policy, so there is nothing for them to regularize.
  const graceMinutes = Number(selectedDayData?.shift?.grace_time) || 0;
  const isLateDay =
    Number(selectedDayData?.late_by_minutes || 0) > graceMinutes;
  const isMissedPunchDay =
    selectedDayData?.status === "absent" ||
    (selectedDayData?.status === "on-leave" &&
      selectedDayData?.is_unpaid_absence);

  /**
   * The day's attendance issues, one row per problem, each with its own
   * regularization state. The backend returns a record per issue; a
   * "forgot to punch in" day has no record yet (the backend creates one on
   * submit), so it is added here as a synthetic row so the employee has
   * something to act on.
   */
  const attendanceIssues = useMemo(() => {
    const records = Array.isArray(selectedDayData?.attendance_issues)
      ? selectedDayData.attendance_issues
      : [];

    const rows = records.map((record) => ({
      _id: record?._id || null,
      event_type: record?.event_type || "late_arrival",
      label: ISSUE_LABELS[record?.event_type] || "Attendance Issue",
      status: record?.regularization?.status || null,
      comments: record?.regularization?.review_comments || "",
    }));

    // Some issues have no LateArrival record of their own yet — the backend
    // creates one on submit. They still need a row here, or the employee has
    // nothing to click.
    const hasRowFor = (eventType) =>
      rows.some((row) => row.event_type === eventType);

    // A late day only gets a LateArrival record when the GRACE PERIOD is
    // exceeded, but `late_by_minutes` is stored for any delay. So a within-grace
    // arrival (and legacy/imported days) is late with no record — still
    // regularizable, and the backend synthesises the record on submit.
    if (isLateDay && !hasRowFor("late_arrival")) {
      rows.unshift({
        _id: null,
        event_type: "late_arrival",
        label: ISSUE_LABELS.late_arrival,
        status: null,
        comments: "",
      });
    }

    // Absent day with no record of its own → offer the missed-punch row.
    if (isMissedPunchDay && !hasRowFor("missed_punch_in")) {
      rows.unshift({
        _id: null,
        event_type: "missed_punch_in",
        label: ISSUE_LABELS.missed_punch_in,
        status: selectedDayData?.regularization?.status || null,
        comments: selectedDayData?.regularization?.review_comments || "",
      });
    }

    return rows;
  }, [selectedDayData, isMissedPunchDay, isLateDay]);

  // Render the regularization section when the day has any issue to act on, OR
  // when a request already exists (so pending/approved/rejected + the final
  // "Regularized" state stay visible even after the day flips to present).
  const showRegularizationSection =
    showRegularizationRequests &&
    (attendanceIssues.length > 0 ||
      isLateDay ||
      !!selectedDayData?.is_regularized);

  // ------------------------------------
  // MONTH RANGE (24 months: last 12 → next 12)
  // ------------------------------------
  const monthsList = useMemo(() => {
    const base = new Date();
    base.setDate(1);

    return Array.from({ length: 24 }, (_, index) => {
      const d = new Date(base);
      d.setMonth(base.getMonth() - 12 + index);
      return d;
    });
  }, []);

  const minMonth = monthsList[0];
  const maxMonth = monthsList[monthsList.length - 1];

  const selectedKey = format(selectedMonth, "yyyy-MM");
  const isMinMonth = selectedKey === format(minMonth, "yyyy-MM");
  const isMaxMonth = selectedKey === format(maxMonth, "yyyy-MM");

  // ------------------------------------
  // HANDLERS
  // ------------------------------------
  const changeMonth = (offset) => {
    const newDate = new Date(selectedMonth);
    newDate.setDate(1);
    newDate.setMonth(newDate.getMonth() + offset);

    setSelectedMonth(newDate);
    fetchCalendar(newDate);
  };

  const minuteOptions = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, "0"),
  );

  const onMonthSelect = (value) => {
    const newDate = new Date(`${value}-01`);
    setSelectedMonth(newDate);
    fetchCalendar(newDate);
  };

  const paginatedRegularizationLogs = useMemo(() => {
    const list = regularizationLogs || [];
    const start = (regPage - 1) * regPageSize;
    return list.slice(start, start + regPageSize);
  }, [regularizationLogs, regPage, regPageSize]);

  if (initialLoading) {
    return <EmployeeAttendanceSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {!employeeId.employeeId && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              My Attendance
            </h1>
            <p className="text-gray-500">
              View your attendance, apply for leave, and log overtime
            </p>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            {/* Who from my department is on approved leave today */}
            <TeamAvailability className="w-full sm:w-auto" />
            {hasFeature("overtime_management") && (
              <Dialog
                open={showOTDialog}
                onOpenChange={(open) => {
                  setShowOTDialog(open);
                  setOtTimeError("");
                  if (open) {
                    getToDaysAttendance();
                    // Reset form with today's date when dialog opens
                    setOTForm({
                      date: format(new Date(), "yyyy-MM-dd"),
                      from_time: "",
                      to_time: "",
                      project: "",
                      notes: "",
                    });
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button className="flex w-full items-center justify-center space-x-2 sm:w-auto">
                    <Clock className="w-4 h-auto mr-2" />
                    Log OT
                  </Button>
                </DialogTrigger>
                <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden border border-border/60 bg-background p-0 shadow-2xl sm:max-w-2xl">
                  <DialogHeader className="border-b border-border  px-6 py-5">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/20">
                        <Clock className="h-5 w-5" />
                      </div>
                      <div className="space-y-1">
                        <DialogTitle className="text-xl font-semibold text-foreground">
                          Log Overtime Hours
                        </DialogTitle>
                        <DialogDescription className="text-sm text-muted-foreground">
                          Capture extra hours with the project, time window, and
                          optional work notes.
                        </DialogDescription>
                      </div>
                    </div>
                  </DialogHeader>
                  <form
                    onSubmit={handleOTSubmit}
                    className="flex flex-1 flex-col overflow-hidden"
                  >
                    <div className="flex-1 space-y-6 overflow-y-auto bg-muted/20 px-6 py-6">
                      <div className="grid gap-6 lg:grid-cols-1">
                        <div className="space-y-5 rounded-2xl border border-border bg-card p-5 shadow-sm">
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-foreground">
                              Date
                            </Label>
                            <DatePickerInput
                              value={otForm.date}
                              minDate={subDays(new Date(), 6)}
                              maxDays={6}
                              onChange={(e) =>
                                setOTForm({ ...otForm, date: e })
                              }
                              title="OT can only be logged for last & days"
                              className="border-border bg-muted/40"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-foreground">
                              Project Name *
                            </Label>
                            <Input
                              type="text"
                              value={otForm.project}
                              onChange={(e) =>
                                setOTForm({
                                  ...otForm,
                                  project: e.target.value,
                                })
                              }
                              placeholder="Enter project name"
                              className="border-border bg-muted/40 focus-visible:ring-primary"
                              required
                            />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                        <div className="mb-4">
                          <h3 className="text-sm font-semibold text-foreground">
                            Time Window
                          </h3>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Select the exact overtime range for this entry.
                          </p>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2 rounded-2xl border border-border bg-muted/30 p-4">
                            <Label className="text-sm font-medium text-foreground">
                              From Time
                            </Label>
                            <div className="grid grid-cols-2 gap-2">
                              <Select
                                value={otForm.from_time.split(":")[0] || ""}
                                onValueChange={(value) => {
                                  const minutes =
                                    otForm.from_time.split(":")[1] || "00";
                                  setOtTimeError("");
                                  setOTForm({
                                    ...otForm,
                                    from_time: `${value}:${minutes}`,
                                  });
                                }}
                              >
                                <SelectTrigger className="border-border bg-background">
                                  <SelectValue placeholder="Hour" />
                                </SelectTrigger>
                                <SelectContent className="max-h-60">
                                  {Array.from({ length: 24 }, (_, i) => (
                                    <SelectItem
                                      key={i}
                                      value={i.toString().padStart(2, "0")}
                                    >
                                      {i.toString().padStart(2, "0")}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select
                                value={otForm.from_time.split(":")[1] || ""}
                                onValueChange={(value) => {
                                  const hour =
                                    otForm.from_time.split(":")[0] || "00";
                                  setOtTimeError("");
                                  setOTForm({
                                    ...otForm,
                                    from_time: `${hour}:${value}`,
                                  });
                                }}
                              >
                                <SelectTrigger className="border-border bg-background">
                                  <SelectValue placeholder="Min" />
                                </SelectTrigger>
                                <SelectContent className="max-h-60">
                                  {minuteOptions.map((minute) => (
                                    <SelectItem key={minute} value={minute}>
                                      {minute}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="space-y-2 rounded-2xl border border-border bg-muted/30 p-4">
                            <Label className="text-sm font-medium text-foreground">
                              To Time
                            </Label>
                            <div className="grid grid-cols-2 gap-2">
                              <Select
                                value={otForm.to_time.split(":")[0] || ""}
                                onValueChange={(value) => {
                                  const minutes =
                                    otForm.to_time.split(":")[1] || "00";
                                  setOtTimeError("");
                                  setOTForm({
                                    ...otForm,
                                    to_time: `${value}:${minutes}`,
                                  });
                                }}
                              >
                                <SelectTrigger className="border-border bg-background">
                                  <SelectValue placeholder="Hour" />
                                </SelectTrigger>
                                <SelectContent className="max-h-60">
                                  {Array.from({ length: 24 }, (_, i) => (
                                    <SelectItem
                                      key={i}
                                      value={i.toString().padStart(2, "0")}
                                    >
                                      {i.toString().padStart(2, "0")}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select
                                value={otForm.to_time.split(":")[1] || ""}
                                onValueChange={(value) => {
                                  const hour =
                                    otForm.to_time.split(":")[0] || "00";
                                  setOtTimeError("");
                                  setOTForm({
                                    ...otForm,
                                    to_time: `${hour}:${value}`,
                                  });
                                }}
                              >
                                <SelectTrigger className="border-border bg-background">
                                  <SelectValue placeholder="Min" />
                                </SelectTrigger>
                                <SelectContent className="max-h-60">
                                  {minuteOptions.map((minute) => (
                                    <SelectItem key={minute} value={minute}>
                                      {minute}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                        {otTimeError && (
                          <p className="mt-4 rounded-xl border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm font-medium text-destructive">
                            {otTimeError}
                          </p>
                        )}
                      </div>

                      <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 to-background p-5 shadow-sm">
                        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                          OT Rules
                        </p>

                        <ul className="list-disc space-y-2 pl-4 text-sm leading-6 text-foreground/90">
                          <li>30-minute intervals only</li>
                          <li>
                            Working days: before{" "}
                            {expectedDate?.shift?.start_time || "--"} or after{" "}
                            {expectedDate?.shift?.end_time || "--"}
                          </li>
                          <li>Weekends/holidays: no restriction</li>
                          <li>Overlapping entries are not allowed</li>
                        </ul>
                      </div>

                      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-foreground">
                            Notes (Optional)
                          </Label>
                          <Textarea
                            value={otForm.notes}
                            onChange={(e) =>
                              setOTForm({ ...otForm, notes: e.target.value })
                            }
                            placeholder="Add a short summary of the work completed during overtime"
                            className="min-h-[120px] resize-none border-border bg-muted/40 focus-visible:ring-primary"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0 border-t border-border bg-background/95 px-6 py-4 backdrop-blur">
                      <Button
                        type="submit"
                        className="h-11 w-full rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/20 transition hover:bg-primary/90"
                        disabled={loading}
                      >
                        Log OT Hours
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      )}

      {/* Monthly Stats */}
      <div
        className={`grid grid-cols-2 md:grid-cols-4 gap-6 ${
          employeeId.employeeId && "mt-10"
        }`}
      >
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Present Days
                </p>
                <p className="md:text-2xl text-md font-bold text-green-600">
                  {employeeCalendarData.summary.present}
                </p>
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12  bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Leave Days</p>
                <p className="md:text-2xl text-md font-bold text-red-600">
                  {employeeCalendarData.summary.absent}
                </p>
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Hours</p>
                <p className="md:text-2xl text-md font-bold text-blue-600">
                  {employeeCalendarData.summary.totalHours}
                </p>
                {/* {employeeCalendarData.summary.otHours > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {employeeCalendarData.summary.otHours}h
                  </p>
                )} */}
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Leave Balance
                </p>
                <p className="md:text-2xl text-md font-bold text-purple-600">
                  {employeeCalendarData.summary.leave_balance}
                </p>
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {selectedDayData && (
        <>
          <Card ref={dayDetailsRef} className="scroll-mt-24">
            <CardHeader>
              <CardTitle>Day {selectedDate} Details</CardTitle>
            </CardHeader>

            <CardContent className="flex flex-col gap-4">
              <div
                className="  grid
    grid-cols-2 gap-3
    md:grid-cols-4
    p-2 bg-muted/30 rounded-lg"
              >
                {/* Status (Special case since it uses a Badge) */}
                <div className="flex flex-col items-center flex-1 min-w-[60px]">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    Status
                  </span>
                  {(() => {
                    // Normalize both sides to LOCAL midnight before comparing
                    // (matching the calendar's isFuture logic). Without this a
                    // UTC-midnight day reads as "future" today, which hid the
                    // status as "--".
                    const selectedDayMidnight = new Date(selectedDayData.date);
                    selectedDayMidnight.setHours(0, 0, 0, 0);
                    const todayMidnight = new Date();
                    todayMidnight.setHours(0, 0, 0, 0);
                    const isFutureSelectedDay =
                      selectedDayMidnight > todayMidnight;

                    // Only a FUTURE scheduled "present" day stays as "--".
                    if (
                      selectedDate &&
                      selectedDayData?.status === "present" &&
                      isFutureSelectedDay
                    ) {
                      return (
                        <span className="text-sm font-medium text-foreground">
                          --
                        </span>
                      );
                    }

                    if (selectedDate && selectedDayData?.status) {
                      return (
                        <Badge
                          className={getStatusColor(selectedDayData.status)}
                        >
                          {selectedDayData.status.charAt(0).toUpperCase() +
                            selectedDayData.status.slice(1)}
                        </Badge>
                      );
                    }

                    return (
                      <span className="text-sm font-medium text-foreground">
                        --
                      </span>
                    );
                  })()}
                </div>

                {/* Shift - Using the DetailColumn component */}
                {selectedDayData.shift?.name && (
                  <DetailColumn
                    label="Shift"
                    value={selectedDayData.shift?.name}
                  />
                )}

                {/* In Time - Using the DetailColumn component */}
                <DetailColumn label="In Time" value={inTime} />

                {/* Out Time - Using the DetailColumn component */}
                <DetailColumn label="Out Time" value={outTime} />

                {/* OT Hours - Using the DetailColumn component with custom class */}
                {selectedDayData.otHours > 0 && (
                  <DetailColumn
                    label="OT Hours"
                    value={`${selectedDayData.otHours}h`}
                    valueClass="text-primary"
                  />
                )}

                {selectedDayData.late_by_minutes > 0 && (
                  <DetailColumn
                    label=" Late by"
                    value={
                      selectedDayData.late_by_minutes > 60
                        ? `${Math.floor(
                            selectedDayData.late_by_minutes / 60,
                          )}h ${selectedDayData.late_by_minutes % 60}m`
                        : `${selectedDayData.late_by_minutes}m`
                    }
                    valueClass="text-primary"
                  />
                )}
              </div>
              {/* 2. FULL-WIDTH ELEMENTS (Notes and Dialog Button) */}
              {selectedDayData.comment && (
                <div className="pt-2 border-t border-border">
                  <span className="text-sm text-muted-foreground">Note</span>
                  <p className="text-sm text-foreground mt-1">
                    {selectedDayData.comment}
                  </p>
                </div>
              )}
              {showRegularizationSection && (
                <>
                  {/* One row per attendance issue on this day. A day with both a
                      late arrival and an early punch-out shows two rows, each
                      with its own status and its own Regularize action. */}
                  <div className="w-full space-y-2">
                    {attendanceIssues.map((issue) => {
                      const isSettled =
                        issue.status === "pending" ||
                        issue.status === "approved";

                      return (
                        <div
                          key={issue._id || issue.event_type}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">
                              {issue.label}
                            </p>
                            {issue.status === "rejected" && (
                              <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">
                                Rejected
                                {issue.comments ? ` — ${issue.comments}` : ""}.
                                You can submit another request.
                              </p>
                            )}
                            {issue.status === "pending" && (
                              <p className="mt-0.5 text-xs text-muted-foreground">
                                Awaiting admin approval
                              </p>
                            )}
                          </div>

                          {isSettled ? (
                            <Badge
                              variant={
                                issue.status === "approved"
                                  ? "success"
                                  : "secondary"
                              }
                            >
                              {issue.status === "approved"
                                ? "Regularized"
                                : "Pending"}
                            </Badge>
                          ) : (
                            !employeeId.employeeId && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-2"
                                onClick={() => {
                                  setActiveIssue(issue);
                                  setRegularizationOpen(true);
                                }}
                              >
                                <FileEdit className="h-3.5 w-3.5" />
                                Regularize
                              </Button>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {selectedDayData?.is_regularized &&
                    attendanceIssues.length === 0 && (
                      <div className="mt-2 flex w-full flex-col items-center gap-1">
                        <Badge variant="success">Regularized</Badge>
                      </div>
                    )}

                  <Dialog
                        open={regularizationOpen}
                        onOpenChange={(open) => {
                          setRegularizationOpen(open);
                          if (!open) setActiveIssue(null);
                        }}
                      >
                        {/* Opened from the per-issue Regularize buttons above,
                            which also set which issue is being submitted. */}
                        <DialogContent className="flex max-w-lg flex-col p-0 overflow-hidden rounded-2xl border border-border bg-background shadow-xl">
                          {/* ===== Header ===== */}
                          <div className="shrink-0 px-6 py-5 border-b border-border bg-muted/40">
                            <DialogTitle className="text-lg font-semibold text-foreground">
                              {activeIssue
                                ? `Regularize ${activeIssue.label}`
                                : "Apply for Regularization"}
                            </DialogTitle>
                            <DialogDescription className="text-sm text-muted-foreground mt-1">
                              Request correction for missed or incorrect
                              attendance records.
                            </DialogDescription>
                          </div>

                          {/* ===== Body ===== */}
                          {/* flex-1 (not a fixed max-h): the body takes whatever
                              height remains inside the dialog's safe-area-capped
                              max height, so the footer buttons always stay
                              visible on small screens (a fixed 65vh could push
                              them past the clipped dialog edge on phones). */}
                          <div className="min-h-0 flex-1 px-6 py-5 space-y-6 overflow-y-auto">
                            {/* Read-only summary */}
                            <div className="grid grid-cols-2 gap-4 rounded-xl border border-border bg-muted/30 p-4">
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">
                                  Date
                                </Label>
                                <DatePickerInput
                                  value={regularizationForm.date}
                                  disabled
                                  className="bg-background"
                                />
                              </div>

                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">
                                  Shift Timing
                                </Label>
                                <div className="flex items-center gap-2 text-sm font-medium">
                                  <span>{regularizationForm.expectedTime}</span>
                                  <span className="text-muted-foreground">
                                    →
                                  </span>
                                  <span>{regularizationForm.outTime}</span>
                                </div>
                              </div>

                              {/* Actual Arrival Time (Derived) */}
                              {selectedDayData?.late_by_minutes > 0 && (
                                <div className="rounded-xl border border-amber-300/40 bg-amber-50 dark:bg-amber-900/20 p-4">
                                  <Label className="text-xs text-amber-700 dark:text-amber-300">
                                    You arrived at
                                  </Label>

                                  <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
                                    <Clock3 className="h-4 w-4" />
                                    {calculateArrivedAtTime(
                                      regularizationForm.expectedTime,
                                      selectedDayData.late_by_minutes,
                                    )}
                                    <span className="text-xs font-normal text-muted-foreground">
                                      ({selectedDayData.late_by_minutes} mins
                                      late)
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Actual punch times — for missed-punch / absent days */}
                            {isMissedPunchDay && (
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                  <Label className="text-sm font-medium">
                                    Actual Punch In
                                  </Label>
                                  <TimeSelect
                                    value={regularizationForm.actualPunchIn}
                                    onChange={(v) =>
                                      handleFormChange("actualPunchIn", v)
                                    }
                                  />
                                </div>
                                <div className="space-y-1.5">
                                  <Label className="text-sm font-medium">
                                    Actual Punch Out
                                  </Label>
                                  <TimeSelect
                                    value={regularizationForm.actualPunchOut}
                                    onChange={(v) =>
                                      handleFormChange("actualPunchOut", v)
                                    }
                                  />
                                </div>
                              </div>
                            )}

                            {/* Reason */}
                            <div className="space-y-1.5">
                              <Label className="text-sm font-medium">
                                Reason
                              </Label>

                              <Select
                                value={regularizationForm.reason || ""}
                                onValueChange={(value) =>
                                  handleFormChange("reason", value)
                                }
                                placeholder="Select reason"
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select reason" />
                                </SelectTrigger>

                                <SelectContent>
                                  <SelectItem value="late">
                                    Late arrival
                                  </SelectItem>
                                  <SelectItem value="missed_punch">
                                    Missed punch
                                  </SelectItem>
                                  <SelectItem value="early_leave">
                                    Left early
                                  </SelectItem>
                                  <SelectItem value="system_issue">
                                    System issue
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Remarks */}
                            <div className="space-y-1.5">
                              <Label className="text-sm font-medium">
                                Remarks <span className="text-red-500">*</span>
                              </Label>

                              <Textarea
                                value={regularizationForm.remarks}
                                onChange={(e) =>
                                  handleFormChange("remarks", e.target.value)
                                }
                                rows={3}
                                placeholder="Briefly explain what happened…"
                                className="resize-none"
                              />
                            </div>

                            {/* Attachment */}
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">
                                Supporting Document (optional)
                              </Label>

                              {!documentFile ? (
                                <label
                                  htmlFor="regularization-file"
                                  className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-muted/30 p-5 text-center cursor-pointer hover:border-primary/50 transition"
                                >
                                  <Upload className="h-6 w-6 text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">
                                    Upload PDF, JPG, or PNG (max 5MB)
                                  </p>
                                  <input
                                    id="regularization-file"
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    className="hidden"
                                    onChange={handleFileUpload}
                                  />
                                </label>
                              ) : (
                                <div className="flex items-center justify-between rounded-xl border border-emerald-300/40 bg-emerald-50 dark:bg-emerald-900/20 p-3">
                                  <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
                                    <FileText className="h-5 w-5" />
                                    <span className="truncate max-w-[220px]">
                                      {documentFile.name}
                                    </span>
                                  </div>

                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setDocumentFile(null)}
                                    className="text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* ===== Footer ===== */}
                          <div className="shrink-0 px-6 py-4 border-t border-border bg-muted/40 flex gap-3">
                            <Button
                              variant="outline"
                              className="flex-1"
                              onClick={() => setRegularizationOpen(false)}
                            >
                              Cancel
                            </Button>

                            <Button
                              className="flex-1"
                              onClick={submitRegularization}
                            >
                              Submit Request
                            </Button>
                          </div>
                        </DialogContent>
                  </Dialog>
                </>
              )}
            </CardContent>
          </Card>
          {(highlights?.events?.length > 0 ||
            highlights?.birthdays?.length > 0 ||
            highlights?.anniversaries?.length > 0 ||
            highlights?.holidays) && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <h3 className="font-bold text-sm sm:text-base text-slate-900 dark:text-white flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-[#3194A0]" />
                    What's Happening
                  </h3>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Today
                  </span>
                </div>

                <div className="p-3 sm:p-4 flex flex-col gap-3">
                  {/* Events Group */}
                  {dayHighlights.events &&
                    dayHighlights.events.map((event, index) => (
                      <div
                        key={`event-${index}`}
                        className="group relative rounded-xl border border-purple-300/40 bg-gradient-to-r from-purple-50/80 to-blue-50/60 dark:from-purple-900/20 dark:to-blue-900/20 p-3 sm:p-4 flex justify-between items-center hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
                      >
                        <div className="flex items-center gap-3 sm:gap-4 min-w-0 z-10">
                          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-white/60 dark:bg-white/10 flex items-center justify-center shadow-sm shrink-0">
                            <AlarmClock className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100 truncate leading-tight">
                              {event.title}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[9px] sm:text-[10px] font-bold text-purple-600/70 dark:text-purple-400/70 uppercase">
                                {event.description}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                  {/* Birthdays Group */}
                  {dayHighlights.birthdays &&
                    dayHighlights.birthdays.map((event, index) => (
                      <div
                        key={`bday-${index}`}
                        className="group relative rounded-xl border border-purple-300/40 bg-gradient-to-r from-purple-50/80 to-blue-50/60 dark:from-purple-900/20 dark:to-blue-900/20 p-3 sm:p-4 flex justify-between items-center hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
                      >
                        <div className="flex items-center gap-3 sm:gap-4 min-w-0 z-10">
                          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-white/60 dark:bg-white/10 flex items-center justify-center shadow-sm shrink-0">
                            <Cake className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100 truncate leading-tight">
                              {event.employee_name}
                            </p>
                            <p className="text-[9px] sm:text-[10px] font-medium text-slate-500 dark:text-slate-400 mt-1 uppercase tracking-wider">
                              Birthday 🎉
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}

                  {/* Anniversaries Group */}
                  {highlights.anniversaries &&
                    highlights.anniversaries.map((event, index) => (
                      <div
                        key={`anniv-${index}`}
                        className="group relative rounded-xl border border-emerald-300/40 bg-gradient-to-r from-emerald-50/80 to-teal-50/60 dark:from-emerald-900/20 dark:to-teal-900/20 p-3 sm:p-4 flex justify-between items-center hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
                      >
                        <div className="flex items-center gap-3 sm:gap-4 min-w-0 z-10">
                          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-white/60 dark:bg-white/10 flex items-center justify-center shadow-sm shrink-0">
                            <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100 truncate leading-tight">
                              {event.employee_name}
                              {currentEmployee &&
                                event.employee_id ===
                                  currentEmployee.employee_id && (
                                  <span className="ml-1 text-emerald-600 dark:text-emerald-300">
                                    (You)
                                  </span>
                                )}
                            </p>
                            <p className="text-[9px] sm:text-[10px] font-medium text-slate-500 dark:text-slate-400 mt-1 uppercase tracking-wider">
                              {event.yearsWorked} Year
                              {event.yearsWorked > 1 ? "s" : ""} Work
                              Anniversary 🎉
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}

                  {dayHighlights.holidays && (
                    <div
                      key={`holiday-${dayHighlights.holidays._id}`}
                      className="group relative rounded-xl border border-purple-300/40 bg-gradient-to-r from-purple-50/80 to-blue-50/60 dark:from-purple-900/20 dark:to-blue-900/20 p-3 sm:p-4 flex justify-between items-center hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
                    >
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0 z-10">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-white/60 dark:bg-white/10 flex items-center justify-center shadow-sm shrink-0">
                          <Palmtree className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100 truncate leading-tight">
                            {dayHighlights.holidays.name}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[9px] sm:text-[10px] font-bold text-emerald-600/70 dark:text-emerald-400/70 uppercase">
                              {new Date(
                                dayHighlights.holidays.date,
                              ).toLocaleDateString("default", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })}
                            </span>
                            <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600" />
                            <span className="text-[9px] sm:text-[10px] font-medium text-slate-500 dark:text-slate-400">
                              {dayHighlights.holidays.description}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Monthly Calendar */}
      <Card>
        <CardHeader>
          <div className="hidden md:flex justify-between items-center">
            <CardTitle>Monthly Attendance Calendar</CardTitle>

            <div className="flex items-center gap-2">
              {/* PREVIOUS */}
              <Button
                variant="outline"
                size="icon"
                disabled={isMinMonth}
                onClick={() => changeMonth(-1)}
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              {/* MONTH DROPDOWN */}
              <Select value={selectedKey} onValueChange={onMonthSelect}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  {monthsList.map((date) => {
                    const value = format(date, "yyyy-MM");
                    return (
                      <SelectItem key={value} value={value}>
                        {format(date, "MMMM yyyy")}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              {/* NEXT */}
              <Button
                variant="outline"
                size="icon"
                disabled={isMaxMonth}
                onClick={() => changeMonth(1)}
                aria-label="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-2 block md:hidden">
            <CardTitle>Monthly Attendance Calendar</CardTitle>
            <div className="flex items-center justify-between mb-4 px-2">
              <div>
                <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 tracking-tight">
                  {selectedMonth.toLocaleString("default", {
                    month: "long",
                    year: "numeric",
                  })}
                </h2>
              </div>
              <div className="flex gap-1.5">
                <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 transition-all">
                  <ChevronLeft
                    className="h-4 w-4 text-slate-600 dark:text-slate-400"
                    onClick={() => changeMonth(-1)}
                  />
                </button>
                <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 transition-all">
                  <ChevronRight
                    className="h-4 w-4 text-slate-600 dark:text-slate-400"
                    onClick={() => changeMonth(1)}
                  />
                </button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-3 mb-4">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
              <div
                key={day}
                className="p-2 text-center font-semibold text-gray-600 dark:text-gray-400 text-sm"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="space-y-2 hidden md:block">{renderCalendar()}</div>
          <div className="space-y-2 block md:hidden">
            {renderMobileCalendar()}
          </div>
          <div className="mt-4 block md:hidden">
            <MobileCalendarLegend />
            {showRegularizationRequests && (
              <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
                <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white"></span>
                <span>
                  <span className="font-semibold text-foreground">
                    Regularize
                  </span>{" "}
                  = request a correction for a wrongly marked day. Tap a day
                  with the amber dot to review and submit a request.
                </span>
              </p>
            )}
          </div>
          {/* Legend */}
          <div className="hidden md:block mt-6 pt-4 border-t border-border">
            <div className="hidden md:flex flex-wrap justify-start gap-x-8 gap-y-4 text-sm">
              <LegendItem
                icon={<CheckCircle className="h-5 w-5 text-emerald-500" />}
                label="Present"
              />

              <LegendItem
                icon={<XCircle className="h-5 w-5 text-rose-500" />}
                label="Absent / On Leave"
              />

              <LegendItem
                icon={
                  <Clock3 className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                }
                label="Half-Day"
              />

              <LegendItem
                icon={<Star className="h-5 w-5 text-sky-500" />}
                label="Holiday"
              />

              <LegendItem
                icon={
                  <AlertCircle className="h-5 w-5  text-orange-600 dark:text-orange-400" />
                }
                label="Late"
              />

              <LegendItem
                icon={<Clock className="h-5 w-5 text-blue-600" />}
                label="Overtime"
              />

              <LegendItem
                icon={<CalendarIcon className="h-5 w-5 text-slate-400" />}
                label="Week-Off / Weekend"
              />

              {showRegularizationRequests && (
                <LegendItem
                  icon={
                    <span className="inline-block h-3 w-3 rounded-full bg-amber-500" />
                  }
                  label="Regularize"
                />
              )}
            </div>

            {showRegularizationRequests && (
              <p className="mt-4 text-left text-xs leading-6 text-muted-foreground">
                <span className="font-semibold text-foreground">
                  Regularize
                </span>
                = request a correction for a wrongly marked day. Click a
                <span className="mx-0.5 inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 align-middle text-[10px] font-bold leading-none text-white">
                  <AlertCircle className="h-2.5 w-2.5" />
                  Regularize
                </span>
                day to review and submit a request.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Events */}
      <Card className="rounded-2xl border border-purple-200/40 bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl shadow-[0_12px_40px_rgba(15,23,42,0.1)] dark:border-purple-900/40">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-purple-700 dark:text-purple-400">
            <Gift className="h-5 w-5" />
            Upcoming Events
          </CardTitle>
        </CardHeader>
        <CardContent className="max-h-[420px] overflow-y-auto pr-2 space-y-3">
          {upcomingEvents.length === 0 && (
            <div className="py-10 text-center text-slate-500 dark:text-slate-400">
              <CalendarIcon className="h-10 w-10 mx-auto mb-2 text-slate-400" />
              No upcoming events
            </div>
          )}
          {upcomingEvents.map((event, index) => (
            <div
              key={index}
              className="rounded-xl border border-purple-300/40 bg-gradient-to-r from-purple-50/80 to-blue-50/60 dark:from-purple-900/20 dark:to-blue-900/20 p-3 flex justify-between items-center hover:shadow-md transition"
            >
              <div className="flex items-start gap-3 min-w-0">
                <div className="text-2xl">{event.icon}</div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                    {event.title}
                    {event.subtitle && (
                      <span className="ml-1 text-purple-600 dark:text-purple-300">
                        {event.subtitle}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {new Date(event.date).toDateString()}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end">
                <p
                  className={`text-lg font-bold ${
                    event.daysUntil === 0
                      ? "text-red-600 dark:text-red-400"
                      : event.daysUntil <= 7
                        ? "text-orange-600 dark:text-orange-400"
                        : "text-purple-700 dark:text-purple-300"
                  }`}
                >
                  {event.daysUntil === 0 ? "Today" : event.daysUntil}
                </p>
                {event.daysUntil > 0 && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {event.daysUntil === 1 ? "day" : "days"}
                  </p>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* OT Logs Summary */}
      <Tabs defaultValue="overtime" className="w-full space-y-6">
        <div className="border-b border-gray-200 dark:border-gray-800 mb-6">
          <TabsList className="grid grid-cols-2 bg-transparent h-auto p-0 overflow-x-auto overflow-y-hidden touch-pan-x no-scrollbar">
            <TabsTrigger value="overtime">Overtime Request</TabsTrigger>
            <TabsTrigger value="regularization">
              Regularization Request
            </TabsTrigger>
          </TabsList>
        </div>

        {/* ===================== OT TAB ===================== */}
        <TabsContent value="overtime" className="space-y-6">
          {/* OT Request Form (if you have one, place above logs) */}
          {otLogs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recent OT Logs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {paginatedOtLogs.map((log) => (
                    <div
                      key={log.id}
                      className="flex justify-between items-start p-4 border rounded-lg hover:bg-muted/40 transition"
                    >
                      <div>
                        <p className="font-medium">
                          {new Date(log.date).toLocaleDateString("default", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </p>

                        <p className="text-sm text-muted-foreground">
                          {log.from_time} - {log.to_time} ({log.ot_hours}h) •{" "}
                          {log.project}
                        </p>

                        {log.notes && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {log.notes}
                          </p>
                        )}
                      </div>

                      <Badge
                        variant={
                          log.status === "approved"
                            ? "default"
                            : log.status === "rejected"
                              ? "destructive"
                              : "secondary"
                        }
                        className="capitalize"
                      >
                        {log.status}
                      </Badge>
                    </div>
                  ))}
                </div>

                <PaginationBar
                  page={otPage}
                  setPage={setOtPage}
                  pageSize={otPageSize}
                  setPageSize={setOtPageSize}
                  totalItems={otLogs?.length || 0}
                  totalPages={totalOtPages}
                  label="OT logs"
                />
              </CardContent>
            </Card>
          )}

          {otLogs.length === 0 && (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                No overtime logs found.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===================== REGULARIZATION TAB ===================== */}
        <TabsContent value="regularization" className="space-y-6">
          {/* Regularization Form */}
          {/* <RegularizationRequestForm /> */}

          {regularizationLogs && regularizationLogs.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Recent Regularization Requests</CardTitle>
              </CardHeader>

              <CardContent>
                <div className="space-y-4">
                  {paginatedRegularizationLogs.map((log) => {
                    const status = log.regularization?.status || "pending";
                    const type = log.event_type || "late_arrival";
                    const typeLabel =
                      type === "missed_punch_in"
                        ? "Missed Punch-In"
                        : type === "early_punch_out"
                          ? "Early Punch-Out"
                          : "Late Arrival";
                    const rawReason = log.regularization?.reason || log.reason;
                    const reason = REASON_LABELS[rawReason] || rawReason;
                    const recordedBy = isObjectId(log.recorded_by)
                      ? "You"
                      : log.recorded_by;
                    // For missed-punch the reason sits in the fields row (below)
                    // so it isn't repeated in the reason/remark box.
                    const reasonInGrid = type === "missed_punch_in";

                    // Type-specific columns (kept to a consistent shape).
                    const fields =
                      type === "missed_punch_in"
                        ? [
                            {
                              label: "Punch In",
                              value: log.actual_check_in || "--",
                            },
                            {
                              label: "Punch Out",
                              value: log.actual_check_out || "--",
                            },
                            {
                              label: "Reason",
                              value: reason || "--",
                              wrap: true,
                            },
                          ]
                        : type === "early_punch_out"
                          ? [
                              {
                                label: "Early Minutes",
                                value: `${log.early_minutes ?? 0} mins`,
                                danger: true,
                              },
                              {
                                label: "Expected Check-Out",
                                value: log.expected_check_out || "--",
                              },
                              {
                                label: "Actual Check-Out",
                                value: log.actual_check_out || "--",
                              },
                            ]
                          : [
                              {
                                label: "Late Minutes",
                                value: `${log.late_minutes ?? 0} mins`,
                                danger: true,
                              },
                              {
                                label: "Expected Check-In",
                                value: log.expected_check_in || "--",
                              },
                              {
                                label: "Actual Check-In",
                                value: log.actual_check_in || "--",
                              },
                            ];

                    return (
                      <div
                        key={log._id}
                        className="border rounded-xl p-5 hover:bg-muted/40 transition space-y-4"
                      >
                        {/* Header: date + type on the left, status on the right */}
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-base">
                                {new Date(log.date).toLocaleDateString(
                                  "default",
                                  {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  },
                                )}
                              </p>
                              <Badge variant="outline" className="text-xs">
                                {typeLabel}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Recorded by {recordedBy || "--"} · Requested{" "}
                              {new Date(log.created_at).toLocaleDateString()}
                            </p>
                          </div>

                          <div className="text-right space-y-1 shrink-0">
                            <Badge
                              variant={
                                status === "approved"
                                  ? "default"
                                  : status === "rejected"
                                    ? "destructive"
                                    : "secondary"
                              }
                              className="capitalize"
                            >
                              {status}
                            </Badge>
                            {log.regularization?.reviewed_at && (
                              <p className="text-xs text-muted-foreground">
                                Reviewed{" "}
                                {new Date(
                                  log.regularization.reviewed_at,
                                ).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Type-specific fields */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                          {fields.map((f) => (
                            <div key={f.label} className="min-w-0">
                              <p className="text-muted-foreground text-xs">
                                {f.label}
                              </p>
                              <p
                                className={`font-medium ${
                                  f.danger ? "text-red-600" : ""
                                } ${f.wrap ? "break-words" : ""}`}
                              >
                                {f.value}
                              </p>
                            </div>
                          ))}
                        </div>

                        {/* Reason / Remarks (reason omitted here for missed-punch,
                            since it's already shown in the fields row above) */}
                        {((reason && !reasonInGrid) ||
                          log.regularization?.remarks) && (
                          <div className="text-sm bg-muted p-3 rounded-md space-y-1">
                            {reason && !reasonInGrid && (
                              <p className="break-words">
                                <span className="font-medium">Reason:</span>{" "}
                                {reason}
                              </p>
                            )}
                            {log.regularization?.remarks && (
                              <p className="break-words">
                                <span className="font-medium">Remark:</span>{" "}
                                {log.regularization.remarks}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <PaginationBar
                  page={regPage}
                  setPage={setRegPage}
                  pageSize={regPageSize}
                  setPageSize={setRegPageSize}
                  totalItems={regularizationLogs?.length || 0}
                  totalPages={totalRegPages}
                  label="regularization requests"
                />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                No regularization requests found.
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmployeeAttendance;

function EmployeeAttendanceSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-44" />
          <Skeleton className="h-4 w-80" />
        </div>
        <Skeleton className="h-10 w-28" />
      </div>
      <div className="grid gap-4 md:grid-cols-4">
        {[...Array(4)].map((_, index) => (
          <Card key={index}>
            <CardContent className="p-5 space-y-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <Skeleton className="h-8 w-52" />
            <Skeleton className="h-10 w-40" />
          </div>
          <div className="grid grid-cols-7 gap-2">
            {[...Array(35)].map((_, index) => (
              <Skeleton
                key={index}
                className="h-16 md:h-20 w-full rounded-xl"
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
